import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Monitor, LogOut, Home } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { useIsMobile } from '../hooks/useIsMobile';

/**
 * When the user is on a mobile viewport, shows a friendly "use desktop" screen
 * instead of Dashboard or Profile. Desktop users see the wrapped children.
 */
function DesktopOnlyGate({ children }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { theme } = useTheme();

  if (!isMobile) {
    return children;
  }

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to log out?')) return;
    try {
      await logout();
    } finally {
      navigate('/', { replace: true });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.bg,
      color: theme.text,
      padding: '24px',
      textAlign: 'center',
    }}>
      <div style={{
        maxWidth: '380px',
        padding: '32px 28px',
        backgroundColor: theme.card,
        borderRadius: '20px',
        border: `1px solid ${theme.border}`,
        boxShadow: theme.shadow,
      }}>
        <div style={{
          width: '72px',
          height: '72px',
          margin: '0 auto 24px',
          borderRadius: '16px',
          backgroundColor: theme.primaryLight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Monitor size={36} color={theme.primary} strokeWidth={2} />
        </div>
        <h1 style={{
          fontSize: '22px',
          fontWeight: '700',
          color: theme.text,
          margin: '0 0 12px 0',
          lineHeight: 1.3,
        }}>
          Use desktop for Dashboard & Profile
        </h1>
        <p style={{
          fontSize: '15px',
          color: theme.textMuted,
          margin: '0 0 28px 0',
          lineHeight: 1.5,
        }}>
          You're signed in as <strong style={{ color: theme.text }}>{user?.email || 'your account'}</strong>. 
          The dashboard and profile are designed for a larger screen. Open this site on your computer for the full experience.
        </p>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              width: '100%',
              padding: '14px 20px',
              fontSize: '15px',
              fontWeight: '600',
              color: theme.text,
              backgroundColor: theme.primaryLight,
              border: `1px solid ${theme.border}`,
              borderRadius: '10px',
              cursor: 'pointer',
            }}
          >
            <Home size={20} />
            Back to home
          </button>
          <button
            type="button"
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              width: '100%',
              padding: '14px 20px',
              fontSize: '15px',
              fontWeight: '500',
              color: theme.textMuted,
              backgroundColor: 'transparent',
              border: `1px solid ${theme.border}`,
              borderRadius: '10px',
              cursor: 'pointer',
            }}
          >
            <LogOut size={18} />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default DesktopOnlyGate;
